/**
 * The studio side of the portal's creator dashboard, which embeds the studio in an iframe (`#embed=1`).
 *
 * studio → portal (`window.parent.postMessage`, targetOrigin = the channel's portal origin, never '*'):
 *   `{type:'omp:studio-ready'}` once the portal origin is known, then every second
 *   `{type:'omp:studio', live, status, viewers, uploadKbps, health, liveSince, droppedFrames, recording}`.
 * portal → studio, accepted only from the portal origin: `{type:'omp:theme', theme:'light'|'dark'}` and
 * `{type:'omp:show-dock', dock:'stream-manager'|'collab'|'tools'}` (unknown docks are ignored).
 * Ad offers (the studio has no login; the portal shows the creator's offers and makes the decisions):
 * portal → studio `{type:'omp:ad-offer', offer, logo?, preview?}` and `{type:'omp:ad-offer-resolved', offer_id, state,
 * decided_surface}`, `{type:'omp:ad-offer-presence', native}`, `{type:'omp:prompter-link', link}`; studio → portal `{type:'omp:ad-offer-decide', offer_id, decision}` (see `postAdOfferDecision`).
 * Nothing is posted while the channel has no usable portal URL. The connect link sets it; otherwise it is the
 * default portal, and a message aimed at an origin the parent does not have is dropped by the browser.
 */
import type { Unsubscribe } from '../../core/services';
import type { StudioApi } from '../../engine/api';
import type { LinkStatus } from '../../net/api';
import { prompterFrameLink } from '../shell/prompterFrame';
import { isShowDock, type ShowDock } from '../../net/connect-link';
import { linkStatusOf } from '../actions/outputs';
import { summarizeHealth, type HealthTone } from '../health/healthModel';
import {
  adOfferDecideMessage,
  parseAdOfferMessage,
  parseAdOfferResolved,
  type OfferDecision,
  type OfferResolved,
  type OfferView,
} from '../panels/ad-offers/model';
import { viewersOf } from '../panels/stream-manager/activityModel';
import { isTheme, type Theme } from '../theme/theme';

export const STATUS_INTERVAL_MS = 1000;

export type EmbedHealth = 'good' | 'warn' | 'bad' | 'unknown';

export interface StudioStatusMessage {
  type: 'omp:studio';
  /** On air: the link is live or reconnecting. */
  live: boolean;
  status: LinkStatus;
  viewers: number;
  uploadKbps: number;
  /** Encoded program bitrate, independent of first-hop peer traffic. Absent outside swarm mode. */
  encodedKbps?: number | null;
  health: EmbedHealth;
  /** Wall-clock ms the stream went live; null while not live. */
  liveSince: number | null;
  droppedFrames: number;
  /** A recording is running or paused; closing the page would cut it short. */
  recording: boolean;
}

export interface StudioReadyMessage {
  type: 'omp:studio-ready';
}

const HEALTH_OF: Record<HealthTone, EmbedHealth> = { idle: 'unknown', healthy: 'good', warning: 'warn', critical: 'bad' };

type StudioLike = Pick<StudioApi, 'runtime' | 'project'>;

/** The status message for the current state; `liveSince` is the caller's record of when the stream went live. */
export function studioStatus(studio: StudioLike, liveSince: number | null): StudioStatusMessage {
  const runtime = studio.runtime.getState();
  const mode = studio.project.getState().project.output.streaming.mode;
  const status = linkStatusOf(mode, runtime);
  const uploadKbps =
    mode === 'swarm' ? (runtime.swarm.stats?.uploadKbps ?? 0) : Object.values(runtime.network).reduce((sum, peer) => sum + peer.outKbps, 0);
  const recording = runtime.recording.status;
  return {
    type: 'omp:studio',
    live: status === 'live' || status === 'reconnecting',
    status,
    viewers: viewersOf(mode, runtime) ?? 0,
    uploadKbps: Math.round(uploadKbps),
    ...(mode === 'swarm' ? { encodedKbps: runtime.swarm.stats?.encodedKbps ?? null } : {}),
    health: HEALTH_OF[summarizeHealth(runtime.health).tone],
    liveSince,
    droppedFrames: mode === 'swarm' ? (runtime.swarm.stats?.droppedFrames ?? 0) : 0,
    recording: recording === 'recording' || recording === 'paused' || recording === 'starting',
  };
}

/** The origin messages go to and come from: the channel's portal URL, or null when it is unset or unusable. */
export function portalOrigin(studio: StudioLike): string | null {
  const url = studio.project.getState().project.channel.portalUrl;
  if (!url) return null;
  try {
    const origin = new URL(url).origin;
    return origin === 'null' ? null : origin;
  } catch {
    return null;
  }
}

export interface EmbedBridgeDeps {
  studio: StudioLike;
  /** The embedding page (`window.parent`). */
  parent: Pick<Window, 'postMessage'>;
  /** Where `message` events arrive (`window`). */
  self: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  onTheme(theme: Theme): void;
  onShowDock(dock: ShowDock): void;
  /** A pending offer of the creator, with its images. */
  onAdOffer?(message: { offer: OfferView; logo?: Blob; preview?: Blob }): void;
  onAdOfferResolved?(resolved: OfferResolved): void;
  /** `{type:'omp:ad-offer-presence', native}`: omp-tools runs and shows the offers natively (the dock stands down). */
  onAdOfferPresence?(native: boolean): void;
  /** Read-only, same-machine presence verified by Studio. Never changes advertising ownership. */
  getToolsPresence?(): boolean | null;
  /** `{type:'omp:prompter-link', link}`: the portal prompter's connection, shown on the OMP Advertising dot. */
  onPrompterLink?(link: 'idle' | 'connecting' | 'connected' | 'disconnected'): void;
  /** Every `omp:prompter-link`: whether it says `'native'` (OMP Tools shows the script; the portal prompter stands down). */
  onPrompterNative?(native: boolean): void;
  onMonetization?(enabled: boolean): void;
  now?: () => number;
  intervalMs?: number;
}

/** Starts talking to the embedding portal; the returned function stops it. */
export function createEmbedBridge({
  studio,
  parent,
  self,
  onTheme,
  onShowDock,
  onAdOffer,
  onAdOfferResolved,
  onAdOfferPresence,
  getToolsPresence,
  onPrompterLink,
  onPrompterNative,
  onMonetization,
  now = Date.now,
  intervalMs = STATUS_INTERVAL_MS,
}: EmbedBridgeDeps): Unsubscribe {
  let liveSince: number | null = null;
  let readyFor: string | null = null;

  const post = (message: StudioStatusMessage | StudioReadyMessage | { type: 'omp:tools-presence'; connected: boolean | null }, origin: string): void => {
    try {
      parent.postMessage(message, origin);
    } catch {
      // A detached or navigated parent; the next tick tries again.
    }
  };

  /** Announces readiness once per portal origin: the connect link may set the portal URL after mount. */
  const announce = (): void => {
    const origin = portalOrigin(studio);
    if (!origin || origin === readyFor) return;
    readyFor = origin;
    post({ type: 'omp:studio-ready' }, origin);
  };

  const tick = (): void => {
    announce();
    const origin = portalOrigin(studio);
    const message = studioStatus(studio, liveSince);
    if (message.live && liveSince === null) liveSince = now();
    else if (!message.live && message.status !== 'connecting') liveSince = null;
    message.liveSince = liveSince;
    if (origin) {
      post(message, origin);
      if (getToolsPresence) post({ type: 'omp:tools-presence', connected: getToolsPresence() }, origin);
    }
  };

  const onMessage = (event: Event): void => {
    const { origin, data } = event as MessageEvent;
    const expected = portalOrigin(studio);
    if (!expected || origin !== expected) return;
    if (!data || typeof data !== 'object') return;
    const { type, theme, dock } = data as { type?: unknown; theme?: unknown; dock?: unknown };
    if (type === 'omp:theme' && isTheme(theme)) onTheme(theme);
    else if (type === 'omp:show-dock' && isShowDock(dock)) onShowDock(dock);
    else if (type === 'omp:ad-offer') {
      const offer = parseAdOfferMessage(data);
      if (offer) onAdOffer?.(offer);
    } else if (type === 'omp:ad-offer-resolved') {
      const resolved = parseAdOfferResolved(data);
      if (resolved) onAdOfferResolved?.(resolved);
    } else if (type === 'omp:monetization') {
      const enabled = (data as { enabled?: unknown }).enabled;
      if ((event as MessageEvent).source === parent && typeof enabled === 'boolean') onMonetization?.(enabled);
    } else if (type === 'omp:prompter-link') {
      const raw = (data as { link?: unknown }).link;
      const link = prompterFrameLink(raw);
      if (link) onPrompterLink?.(link);
      onPrompterNative?.(raw === 'native');
    } else if (type === 'omp:ad-offer-presence') {
      const { native } = data as { native?: unknown };
      if (typeof native === 'boolean') onAdOfferPresence?.(native);
    }
  };

  self.addEventListener('message', onMessage);
  const unsubscribe = studio.project.subscribe(announce);
  announce();
  const timer = setInterval(tick, intervalMs);

  return () => {
    clearInterval(timer);
    unsubscribe();
    self.removeEventListener('message', onMessage);
  };
}

/**
 * The creator's answer to an offer, for the portal to make with its login. Only the offer and the answer go: who
 * answered and from where is the portal's to say. False when there is no portal origin to send it to.
 */
export function postAdOfferDecision(parent: Pick<Window, 'postMessage'>, studio: StudioLike, offerId: string, decision: OfferDecision): boolean {
  const origin = portalOrigin(studio);
  if (!origin) return false;
  try {
    parent.postMessage(adOfferDecideMessage(offerId, decision), origin);
    return true;
  } catch {
    return false;
  }
}
